import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Leaf, LogOut, Plus, HomeIcon as Home, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandMark from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AIChatPanel from "@/components/AIChatPanel";

/**
 * Dashboard — state-aware.
 * No fake, seeded, or demo-constant data is rendered. Metrics only exist
 * when real telemetry (device_readings) is present. Monetary values use AED.
 */
export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState({ loading: true, error: null, overview: null });

  async function load() {
    setStatus((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/home/overview");
      setStatus({ loading: false, error: null, overview: data });
    } catch (e) {
      setStatus({ loading: false, error: e.message || "Could not load dashboard", overview: null });
    }
  }

  useEffect(() => { load(); }, []);

  const state = status.overview?.state;

  return (
    <div className="min-h-screen bg-bone">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bone/85 border-b border-[#E2DFD8]" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <BrandMark variant="light" size="sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/dashboard" className="text-forest font-medium" data-testid="nav-dashboard-link">Dashboard</Link>
            <Link to="/devices" className="text-ink/70 hover:text-forest" data-testid="nav-devices-link">Devices</Link>
          </nav>
          <div className="flex items-center gap-4">
            {state === "ready" && (
              <span className="hidden sm:flex items-center gap-2 text-xs font-mono text-forest/70">
                <span className="w-2 h-2 rounded-full bg-moss animate-pulse" />
                live
              </span>
            )}
            <span className="hidden sm:inline text-sm text-ink/70">Hi, {user?.name?.split(" ")[0] || "there"}</span>
            <Button variant="ghost" onClick={async () => { await logout(); nav("/"); }} className="rounded-full" data-testid="dashboard-logout">
              <LogOut size={16} strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {status.loading && <LoadingState />}
        {!status.loading && status.error && <ErrorState onRetry={load} message={status.error} />}
        {!status.loading && !status.error && state === "no_home" && <NoHomeState />}
        {!status.loading && !status.error && state === "no_devices" && <NoDevicesState home={status.overview.home} />}
        {!status.loading && !status.error && state === "waiting_telemetry" && (
          <WaitingTelemetryState home={status.overview.home} devices={status.overview.devices} />
        )}
        {!status.loading && !status.error && state === "ready" && (
          <ReadyDashboardView overview={status.overview} />
        )}
      </main>

      {state === "ready" && <AIChatPanel />}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="surface-card p-16 text-center" data-testid="dashboard-loading">
      <p className="font-serif text-2xl text-forest animate-pulse">Loading…</p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="surface-card p-12 text-center" data-testid="dashboard-error">
      <p className="label-eyebrow mb-3">Something went wrong</p>
      <p className="font-serif text-3xl text-forest">We couldn't load your dashboard.</p>
      <p className="mt-2 text-sm text-ink/55">{message}</p>
      <Button onClick={onRetry} className="mt-6 rounded-full bg-forest hover:bg-forest-hover text-bone px-6">
        Try again
      </Button>
    </div>
  );
}

function NoHomeState() {
  return (
    <div className="surface-card p-12 md:p-16 text-center max-w-2xl mx-auto" data-testid="state-no-home">
      <div className="w-14 h-14 rounded-full bg-forest/5 text-forest grid place-items-center mx-auto mb-6">
        <Leaf size={22} strokeWidth={1.5} />
      </div>
      <p className="label-eyebrow mb-2">Welcome to Noa</p>
      <h2 className="font-serif text-3xl md:text-4xl text-forest font-light">
        Set up your home and connect your first device to start monitoring.
      </h2>
      <Link to="/onboarding/home" className="inline-block mt-8" data-testid="cta-set-up-home">
        <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 h-12">
          Set up my home
        </Button>
      </Link>
    </div>
  );
}

function NoDevicesState({ home }) {
  return (
    <div className="surface-card p-12 md:p-16 text-center max-w-2xl mx-auto" data-testid="state-no-devices">
      <div className="w-14 h-14 rounded-full bg-forest/5 text-forest grid place-items-center mx-auto mb-6">
        <Home size={22} strokeWidth={1.5} />
      </div>
      <p className="label-eyebrow mb-2">{home?.name}</p>
      <h2 className="font-serif text-3xl md:text-4xl text-forest font-light">No devices connected yet</h2>
      <p className="mt-3 text-ink/60 max-w-md mx-auto">
        Connect your first device to start monitoring your home.
      </p>
      <Link to="/devices" className="inline-block mt-8" data-testid="cta-add-device">
        <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 h-12">
          <Plus size={16} className="mr-2" /> Add device
        </Button>
      </Link>
    </div>
  );
}

function WaitingTelemetryState({ home, devices }) {
  return (
    <div className="max-w-4xl mx-auto" data-testid="state-waiting-telemetry">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="label-eyebrow">{home?.name}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-forest font-light mt-1">Waiting for device data</h1>
        </div>
        <Link to="/devices">
          <Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-bone">
            Manage devices
          </Button>
        </Link>
      </div>

      <div className="surface-card p-6 md:p-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <PlaceholderStat label="Power" />
          <PlaceholderStat label="Water" />
          <PlaceholderStat label="CO₂ avoided" />
          <PlaceholderStat label="Active devices" value={`0/${devices.length}`} />
        </div>
        <div className="rounded-2xl bg-bonewarm border border-[#E2DFD8] p-5 flex items-start gap-3">
          <Wifi size={18} className="text-forest mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="font-serif text-lg text-ink">No telemetry received yet</p>
            <p className="text-sm text-ink/60 mt-1 max-w-xl leading-relaxed">
              Your devices are registered, but Noa hasn't received any live readings. Real metrics will appear here once your connected devices start reporting.
            </p>
          </div>
        </div>
        <div className="mt-6">
          <p className="label-eyebrow mb-3">Registered devices</p>
          <ul className="space-y-2" data-testid="waiting-device-list">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between bg-white rounded-xl border border-[#E2DFD8] px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{d.name}</p>
                  <p className="text-[11px] font-mono text-ink/50">{d.category}{d.room ? ` · ${d.room}` : ""} · provider: {d.provider || "manual"}</p>
                </div>
                <span className="text-xs text-ink/50 font-mono">—</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PlaceholderStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#E2DFD8] bg-white p-5">
      <p className="label-eyebrow">{label}</p>
      <p className="font-serif text-3xl text-forest/70 font-light mt-2">{value ?? "—"}</p>
      <p className="text-[11px] text-ink/40 mt-1">no data</p>
    </div>
  );
}

/**
 * ReadyDashboardView — rendered only when real telemetry exists.
 * Phase 4 will fully populate the charts; this view currently shows the
 * server-computed live power/water with the preserved layout and AED currency.
 */
function ReadyDashboardView({ overview }) {
  const m = overview.metrics;
  return (
    <div data-testid="state-ready">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="label-eyebrow mb-2">{overview.home?.name} · live</p>
          <h1 className="font-serif text-4xl md:text-5xl text-forest font-light tracking-tight">
            Your home is reporting live telemetry.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-row">
        <StatCard label="Power right now" value={`${m?.current_watts ?? 0} W`} />
        <StatCard label="Water flow" value={`${m?.current_liters_per_hour ?? 0} L/h`} />
        <StatCard label="Active devices" value={`${m?.active_devices ?? 0}/${m?.total_devices ?? 0}`} />
        <StatCard label="Status" value={m?.live ? "LIVE" : "idle"} accent={m?.live} />
      </div>

      <div className="surface-card p-6 md:p-8">
        <p className="label-eyebrow">Historical analytics</p>
        <h3 className="font-serif text-2xl mt-1">Last 14 days</h3>
        <p className="mt-4 text-sm text-ink/55">Historical charts will appear here once Noa has aggregated enough readings. All values use AED where applicable.</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? "bg-forest text-bone border-forest" : "bg-white border-[#E2DFD8]"}`}>
      <p className={`label-eyebrow ${accent ? "text-bone/60" : ""}`}>{label}</p>
      <p className="font-serif text-3xl mt-2 font-light">{value}</p>
    </div>
  );
}
